import { Injectable } from '@angular/core';
import { catchError, map, Observable, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { InputField, ProviderName } from '../../../account/models/login-response.model';
import { ErrorStateService } from '../../../shared/services/core/error.service';
import { LoggingService } from '../../../shared/services/core/logging.service';
import { ApiService } from '../../../shared/services/core/api.service';
import { FitnessTracker } from './fitness-tracker.model';

@Injectable({
  providedIn: 'root',
})
export class FitnessTrackerService {
  constructor(
    private api: ApiService,
    private errorService: ErrorStateService,
    private loggingService: LoggingService
  ) { }

  /**
   * Retrieves the list of available fitness tracker providers
   * and their current connection status for the logged-in user.
   *
   * @returns Observable containing the fitness tracker providers and
   * their connection details.
   */
  getConnectedProvider(): Observable<FitnessTracker[]> {
    return this.api.get<FitnessTracker>('provider/externalConnect');
  }

  /**
   * Initiates a connection with the specified external fitness tracker provider.
   *
   * The supplied login parameters are sent to the API to generate the provider's
   * authorization URL. If the provider uses an OAuth flow, the user is redirected
   * to the generated authorization URL to complete the connection.
   *
   * @param providerName The external fitness tracker provider to connect to.
   * @param loginUrlParameters The parameters required to generate the provider's
   * authorization URL.
   * @param clubId The club identifier. Defaults to 1.
   * @returns The response returned by the API.
   */
  async externalConnectionToProvider(
    providerName: ProviderName,
    loginUrlParameters: InputField[],
    clubId: number = 1
  ) {
    const response = await this.api
      .post(`provider/externalConnect/${providerName}/${clubId}`, loginUrlParameters)
      .pipe(
        tap((generatedUrl) => {
          if (generatedUrl?.Data?.IsOAuthConnect) {
            window.location.href = generatedUrl.Data.Url;
          }
        }),
        catchError((err: HttpErrorResponse) => {
          this.errorService.clearErrors();
          this.loggingService.error(err.error.toString());
          this.errorService.addError(err.error.toString());
          throw err;
        })
      )
      .toPromise();

    return response;
  }

  /**
 * Validates the callback response received from an external
 * fitness tracker provider.
 *
 * The provider id and callback parameters are forwarded to the API
 * to complete the OAuth authentication/connection process.
 *
 * @param providerName The external fitness tracker provider.
 * @param loginUrlParameters The callback parameters returned by the provider.
 * @param clubId The club identifier. Defaults to 1.
 * @returns Observable<boolean> indicating whether the callback was successfully validated.
 */
  validateCode(
    providerName: ProviderName,
    loginUrlParameters: InputField[],
    clubId: number = 1
  ) {
    return this.api
      .post(`provider/externalConnect/${providerName}/${clubId}/code`, loginUrlParameters)
      .pipe(
        map((authResponse: any) => !!authResponse)
      );
  }

  async externalRevokeToProvider(
    providerName: ProviderName,
    clubId: number = 1
  ) {
    const response = await this.api
      .post(`provider/externalConnect/${providerName}/${clubId}/revoke`, null)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.errorService.clearErrors();
          this.loggingService.error(err.error.toString());
          this.errorService.addError(err.error.toString());
          throw err;
        })
      )
      .toPromise();

    return response;
  }
}